import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    DataGrid,
    GridActionsCellItem,
    GridRowEditStopReasons,
    GridRowModes,
} from "@mui/x-data-grid";
import {
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import useApiData from "../../services/useApiData.js";
import PutData from "../../services/PutData.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import { BUYER_PRODUCT_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import {
    catalogDataGridSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "./catalogGridStyles.js";

function moneyText(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function toNumberOrNull(value) {
    if (value == null || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function brandName(value) {
    if (!Array.isArray(value)) return "";
    return value
        .map((entry) => (entry && typeof entry === "object" ? entry.name || "" : ""))
        .filter(Boolean)
        .join(", ");
}

function mapProductRow(product) {
    return {
        id: product._id,
        listingField: product.listingField || "",
        itemName: product.itemName || "",
        brand: brandName(product.brand),
        internalSkuId: product.internalSkuId || "",
        vistaMaxPrice: product.vistaMaxPrice ?? null,
        macMaxPrice: product.macMaxPrice ?? null,
        bidftaMaxPrice: product.bidftaMaxPrice ?? null,
        triangleAndersonPrice: product.triangleAndersonPrice ?? null,
        ffPricing: product.ffPricing ?? null,
        fbMktPricing: product.fbMktPricing ?? null,
        maxPurchasePrice: product.maxPurchasePrice ?? null,
        suggestedEbayPrice: product.suggestedEbayPrice ?? null,
        suggestedWalmartPrice: product.suggestedWalmartPrice ?? null,
        msrpUsd: product.msrpUsd ?? null,
        suggestedMargin: product.suggestedMargin ?? null,
    };
}

function MoneyCell({ value }) {
    const text = moneyText(value);
    return (
        <Typography
            component="span"
            sx={{
                fontSize: 13,
                fontWeight: 600,
                color: text ? "#1c2430" : "#94a3b8",
                fontVariantNumeric: "tabular-nums",
            }}
        >
            {text || "—"}
        </Typography>
    );
}

function moneyColumn(field, headerName, width = 130, editable = false) {
    return {
        field,
        headerName,
        width,
        minWidth: 110,
        editable,
        type: editable ? "number" : undefined,
        align: "right",
        headerAlign: "right",
        sortable: true,
        renderCell: (params) => <MoneyCell value={params.value} />,
    };
}

export default function BuyerProductGrid() {
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [searchInput, setSearchInput] = useState("");
    const [searchQ, setSearchQ] = useState("");
    const [rowModesModel, setRowModesModel] = useState({});
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });

    const filterFields = useMemo(() => BUYER_PRODUCT_FILTER_FIELDS(), []);
    const {
        activeConditions,
        conjunction,
        barProps: filterBar,
    } = useGridFilter(filterFields);

    const layout = useGridLayout(GRID_LAYOUT_KEYS.buyerProducts, {
        sortModel: [{ field: "listingField", sort: "asc" }],
        columnVisibilityModel: {},
    });

    const editingId = useMemo(() => {
        const entry = Object.entries(rowModesModel).find(
            ([, mode]) => mode?.mode === GridRowModes.Edit
        );
        return entry ? entry[0] : null;
    }, [rowModesModel]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQ(searchInput.trim());
            setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const filtersParam = useMemo(() => {
        if (!activeConditions.length) return "";
        return JSON.stringify({
            conjunction,
            conditions: activeConditions.map(({ field, operator, value }) => ({
                field,
                operator,
                value,
            })),
        });
    }, [activeConditions, conjunction]);

    const sortForApi = useMemo(() => {
        const entry = layout.sortModel?.[0];
        if (entry?.field) {
            return {
                sort: entry.field,
                sortDir: entry.sort === "asc" ? "asc" : "desc",
            };
        }
        return { sort: "listingField", sortDir: "asc" };
    }, [layout.sortModel]);

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [filtersParam, conjunction, searchQ]);

    const listUrl = useMemo(() => {
        const params = new URLSearchParams({
            page: String(paginationModel.page + 1),
            pageSize: String(paginationModel.pageSize),
            sort: sortForApi.sort,
            sortDir: sortForApi.sortDir,
        });
        if (searchQ) params.set("q", searchQ);
        if (filtersParam) params.set("filters", filtersParam);
        return `api/broylesllc/products?${params.toString()}`;
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        searchQ,
        filtersParam,
        sortForApi.sort,
        sortForApi.sortDir,
    ]);

    const { data, error, load, isLoading, refetch } = useApiData(listUrl);

    useEffect(() => {
        const items = data?.items || [];
        setRowCount(typeof data?.total === "number" ? data.total : items.length);
        setRows(items.map(mapProductRow));
    }, [data]);

    const startEdit = useCallback((id) => {
        setRowModesModel({ [id]: { mode: GridRowModes.Edit, fieldToFocus: "vistaMaxPrice" } });
    }, []);

    const cancelEdit = useCallback(() => {
        if (!editingId) return;
        setRowModesModel({
            [editingId]: { mode: GridRowModes.View, ignoreModifications: true },
        });
    }, [editingId]);

    const saveEdit = useCallback(() => {
        if (!editingId) return;
        setRowModesModel({
            [editingId]: { mode: GridRowModes.View },
        });
    }, [editingId]);

    const processRowUpdate = useCallback(async (newRow, oldRow) => {
        const vistaMaxPrice = toNumberOrNull(newRow.vistaMaxPrice);
        const msrpUsd = toNumberOrNull(newRow.msrpUsd);
        const vistaChanged = vistaMaxPrice !== toNumberOrNull(oldRow.vistaMaxPrice);
        const msrpChanged = msrpUsd !== toNumberOrNull(oldRow.msrpUsd);
        if (!vistaChanged && !msrpChanged) return oldRow;

        setSaving(true);
        const result = await PutData(
            { vistaMaxPrice, msrpUsd },
            `broylesllc/products/${newRow.id}`
        );
        setSaving(false);
        if (!result.success) {
            const message =
                result?.data?.message ||
                result?.error?.response?.data?.message ||
                "Save failed.";
            setSnack({ open: true, color: "error", message });
            throw new Error(message);
        }
        const updated = mapProductRow(result.data);
        setRows((prev) =>
            prev.map((row) => (String(row.id) === String(updated.id) ? updated : row))
        );
        setSnack({ open: true, color: "success", message: "Product saved." });
        return updated;
    }, []);

    const columns = useMemo(
        () => [
            {
                field: "actions",
                type: "actions",
                headerName: "",
                width: 52,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                getActions: ({ id }) => {
                    const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;
                    if (isEditing) {
                        return [
                            <GridActionsCellItem
                                key="cancel"
                                icon={<CloseOutlinedIcon fontSize="small" />}
                                label="Cancel"
                                onClick={cancelEdit}
                                color="inherit"
                            />,
                        ];
                    }
                    return [
                        <GridActionsCellItem
                            key="edit"
                            icon={<EditOutlinedIcon fontSize="small" />}
                            label="Edit"
                            onClick={() => startEdit(id)}
                            color="inherit"
                            disabled={Boolean(editingId) && String(editingId) !== String(id)}
                        />,
                    ];
                },
            },
            {
                field: "listingField",
                headerName: "Listing Field",
                flex: 1.4,
                minWidth: 220,
                sortable: true,
            },
            moneyColumn("vistaMaxPrice", "Vista Max Price", 130, true),
            moneyColumn("macMaxPrice", "MAC Max Price"),
            moneyColumn("bidftaMaxPrice", "BidFTA Max Price"),
            moneyColumn("triangleAndersonPrice", "Triangle Anderson Price", 150),
            moneyColumn("ffPricing", "F+F Pricing", 110),
            moneyColumn("fbMktPricing", "FB MKT Pricing", 120),
            {
                field: "maxPurchasePrice",
                headerName: "Max Purchase Price",
                width: 150,
                minWidth: 140,
                align: "right",
                headerAlign: "right",
                sortable: true,
                renderCell: (params) => {
                    const text = moneyText(params.value);
                    return (
                        <Chip
                            size="small"
                            label={text || "—"}
                            sx={{
                                height: 26,
                                fontWeight: 700,
                                fontSize: 12,
                                borderRadius: "999px",
                                backgroundColor: "#e0f2fe",
                                color: "#0369a1",
                            }}
                        />
                    );
                },
            },
            moneyColumn("suggestedEbayPrice", "Suggested eBay Price", 140),
            moneyColumn("suggestedWalmartPrice", "Suggested Walmart Price", 150),
            moneyColumn("msrpUsd", "MSRP (USD)", 120, true),
            moneyColumn("suggestedMargin", "Suggested Margin", 130),
        ],
        [cancelEdit, editingId, rowModesModel, startEdit]
    );

    return (
        <>
            {error ? (
                <Typography color="error" sx={{ mb: 1, fontSize: 13, px: 2 }}>
                    Failed to load products.
                </Typography>
            ) : null}
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search listing, SKU, brand…"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {editingId ? (
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0369a1" }}>
                            Editing row
                        </Typography>
                    ) : null}
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={columns} />
                    {editingId ? (
                        <>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={cancelEdit}
                                disabled={saving}
                                sx={catalogQuietButtonSx}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={saveEdit}
                                disabled={saving}
                                startIcon={
                                    saving ? (
                                        <CircularProgress size={14} color="inherit" />
                                    ) : (
                                        <SaveOutlinedIcon fontSize="small" />
                                    )
                                }
                                sx={catalogPrimaryButtonSx}
                            >
                                Save
                            </Button>
                        </>
                    ) : null}
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(load || isLoading)}
                        rows={rows}
                        columns={columns}
                        editMode="row"
                        rowModesModel={rowModesModel}
                        onRowModesModelChange={setRowModesModel}
                        onRowEditStop={(params, event) => {
                            if (params.reason === GridRowEditStopReasons.rowFocusOut) {
                                event.defaultMuiPrevented = true;
                            }
                        }}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={() => {}}
                        disableRowSelectionOnClick
                        paginationMode="server"
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        sortingMode="server"
                        sortModel={layout.sortModel}
                        onSortModelChange={layout.onSortModelChange}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        slotProps={appDataGridSlotProps}
                        sx={catalogDataGridSx}
                    />
                </div>
            </div>

            {snack.open ? (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            ) : null}
        </>
    );
}
